/**
 * Centralized Feature Flag Keys
 * 
 * This file contains all feature flag keys used throughout the application.
 * Using constants ensures consistency and makes flags easier to discover and maintain.
 * 
 * Usage:
 * import { FEATURE_FLAGS } from '../config/featureFlags';
 * const isEnabled = useFeatureFlag(FEATURE_FLAGS.INTEGRATIONS_UI);
 */

export const FEATURE_FLAGS = {
  INTEGRATIONS_UI: 'integrations_ui',
  SUB_TERMS: 'subterm_implementation',
  STUDENT_PORTAL_WIDGETS: 'student_portal_widgets',
  GLOBAL_SEARCH_AUTOCOMPLETE: 'global_search_autocomplete_enabled',
  SSN_ENCRYPTION_REMOVAL: 'ssn_encryption_removal',
  FRONTEND_MEMORY_HYGIENE_ROUND2: 'frontend_memory_hygiene_round2',
  TX_CBM_REPORTS: 'tx_cbm_reports',
  DATA_DICTIONARY: 'data_dictionary',
  STUDENT_LIFE_PARKING: 'student_life_parking',
  QUERY_BUILDER: 'query_builder',
  STUDENT_LIFE_ACCESSIBILITY_SERVICES: 'student_life_accessibility_services',
  STUDENT_LIFE_DA_DOCUMENT_TRACKING: 'student_life_disability_accommodations_document_tracking',
  BILLING_1098T_ELECTRONIC_DELIVERY: 'billing_1098t_electronic_delivery',
  ADMISSIONS_TEST_SCORE_RESULT_TYPE: 'admissions_test_score_result_type',
  // Split Aristotle gates: the full-page Home experience and the floating
  // per-page launcher icon toggle independently.
  ARISTOTLE_HOME: 'aristotle_assistant_home',
  ARISTOTLE_WIDGET: 'aristotle_assistant_widget',
  // The PowerFAIDS drop-off/pick-up addresses, access key and setup instructions. Off by default:
  // the folder view covers the common case, and the API is only wanted by institutions building
  // their own transfer.
  POWERFAIDS_FILE_EXCHANGE_API: 'powerfaids_file_exchange_api',
  // Degree Planning master switch: the sidebar menu and the student record's Degree Plan tab.
  DEGREE_PLANNING_MODULE: 'degree_planning_module',
};

/**
 * Unique CAMSMenu.MenuItemName of the Texas State Reports wizard, used to filter the
 * sidebar menu list client-side. Compare trimmed — CAMSMenu pads names with
 * leading spaces to encode menu depth.
 */
export const TX_CBM_WIZARD_MENU_ITEM_NAME = 'CBM Reporting Wizard';

/**
 * CAMSMenu.MenuItemName of the Data Dictionary, used to filter the sidebar menu list
 * client-side. Compare trimmed — CAMSMenu pads names with leading spaces to encode depth.
 */
export const DATA_DICTIONARY_MENU_ITEM_NAME = 'Data Dictionary';

/**
 * CAMSMenu.MenuItemName of the Degree Planning root. Its children are named
 * "Degree Planning-<page>" (for example "Degree Planning-Plans"), so the sidebar hides
 * the root and every name starting with this plus "-". Compare trimmed: CAMSMenu pads
 * names with leading spaces to encode depth.
 */
export const DEGREE_PLANNING_MENU_ITEM_NAME = 'Degree Planning';

/**
 * Glossary category (GlossaryCategory.ClassificationCBM) that holds the THECB Classification
 * codes. The System Manager glossary list hides this category entirely while the CBM Reporting
 * Wizard flag is off, so its system-defined rows cannot be edited or deleted by a tenant that
 * is not doing Texas CBM reporting.
 */
export const TX_CBM_CLASSIFICATION_GLOSSARY_ELEMENT_NO = 2283;

/**
 * CAMSMenu.MenuItemName prefix shared by the Student Life > Parking parent row and every child
 * row (e.g. 'Student Life-Parking-Lots-Manage'). The sidebar hides every item with this prefix
 * while the parking flag is off. Compare trimmed — CAMSMenu pads names with leading spaces.
 */
export const PARKING_MENU_ITEM_NAME_PREFIX = 'Student Life-Parking';

/**
 * CAMSMenu.MenuItemName of the Query Builder, used to filter the sidebar menu list
 * client-side. Compare trimmed — CAMSMenu pads names with leading spaces to encode depth.
 */
export const QUERY_BUILDER_MENU_ITEM_NAME = 'Query Builder';

/**
 * CAMSMenu.MenuItemName prefix shared by the Student Life > Accessibility Services (Disability
 * Accommodations) parent row and its '-Manage' child. Hidden in the sidebar while the
 * accessibility services flag is off. Compare trimmed.
 */
export const ACCESSIBILITY_SERVICES_MENU_ITEM_NAME_PREFIX = 'Student Life-Disability Accommodations';

export const DA_DOCUMENT_TRACKING_MENU_ITEM_NAME = 'Student Life-Disability Accommodations-Document Tracking';

/**
 * CAMSMenu.MenuItemName prefix shared by the Billing > 1098-T Electronic Delivery parent row and its
 * children. Hidden in the sidebar while the 1098-T Electronic Delivery flag is off. Compare trimmed.
 */
export const FORM_1098T_ELECTRONIC_DELIVERY_MENU_ITEM_NAME_PREFIX = 'Billing-1098T Electronic Delivery';
